import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "../components/common/toast";
import { z } from "zod";
import { companyService } from "../services/api";
import {
  Table,
  FormPopup,
  EditIcon,
  // DeleteIcon,
  CompanyDetailsModal,
} from "../components/common";
import SkeletonTable from "../components/SkeletonTable";
import {
  INDIA_STATES,
  getCitiesForState,
  isCityInState,
} from "../utils/indiaStateCity";

function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    const selected = options.find((o) => o.value === value);
    if (!open && selected) {
      setQuery(selected.label);
    }
  }, [value, options, open]);

  const handleInputFocus = () => {
    if (disabled) return;
    // Always start dropdown with full list; user can type to filter.
    setQuery("");
    setOpen(true);
  };

  const handleSelect = (opt) => {
    if (onChange) {
      onChange({ target: { value: opt?.value ?? "" } });
    }
    setOpen(false);
  };

  const lowerQuery = query.toLowerCase();
  const filtered =
    lowerQuery.trim().length === 0
      ? options
      : options.filter((opt) => {
          const label = String(opt.label ?? "").toLowerCase();
          const val = String(opt.value ?? "").toLowerCase();
          return label.includes(lowerQuery) || val.includes(lowerQuery);
        });

  const selectedLabel = value
    ? options.find((o) => o.value === value)?.label ?? ""
    : "";

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        value={open ? query : selectedLabel || ""}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full min-w-0 rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] overflow-visible ${
          disabled ? "cursor-not-allowed opacity-60" : ""
        }`}
        style={{ height: 44 }}
      />
      {!disabled && (
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`h-4 w-4 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </div>
      )}
      {open && !disabled && (
        <div className="absolute z-[99999] left-0 right-0 mt-1 rounded-[10px] border border-[#D0D5DD] bg-white shadow-lg overflow-hidden">
          <div
            className="overflow-y-auto overscroll-contain py-1"
            style={{ maxHeight: 220 }}
          >
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">No results</div>
            )}
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-[#F6F7FA] ${
                  value === opt.value
                    ? "bg-[rgba(24,135,66,0.08)] text-[#188742]"
                    : "text-gray-800"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const INDIAN_GSTIN_REGEX =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
// OLD: phone regex allowed 7–15 digits.
// const PHONE_REGEX = /^[0-9]{7,15}$/;
// NEW: phone must be exactly 10 digits (primary + secondary).
const PHONE_REGEX = /^[0-9]{10}$/;
// NEW: name must contain alphabets and spaces only (no digits or special chars).
const NAME_REGEX = /^[A-Za-z\s]+$/;
// NEW: basic email validation for secondary customer (primary already uses zod .email()).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const extractPanFromGstin = (gstin) => {
  const v = String(gstin || "").trim().toUpperCase();
  if (v.length !== 15) return "";
  // Characters 3..12 (1-based) => slice(2, 12) (0-based, end-exclusive)
  return v.slice(2, 12);
};

const COMPANY_SAVE_GENERIC_ERROR =
  "Something went wrong. Please try again";

function companySaveErrorMessage(error) {
  const m =
    error?.response?.data?.message ?? error?.response?.data?.error;
  if (typeof m === "string" && m.trim()) return m.trim();
  return COMPANY_SAVE_GENERIC_ERROR;
}

const companyFormSchema = z.object({
  companyCode: z.string().optional(),
  // OLD: only non-empty check for name.
  // name: z.string().min(1, "Company name is required"),
  // NEW: require alphabets (and spaces) only for primary company name.
  name: z
    .string()
    .min(1, "Company name is required")
    .refine(
      (v) => NAME_REGEX.test(v.trim()),
      "Company name can contain alphabets and spaces only"
    ),
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
  phone: z
    .string()
    .min(1, "Phone is required")
    .refine(
      (v) => PHONE_REGEX.test(v.trim()),
      "Enter a valid 10-digit phone number"
    ),
  gstin: z
    .string()
    .min(1, "GSTIN is required")
    .refine(
      (v) => INDIAN_GSTIN_REGEX.test(v.trim().toUpperCase()),
      "Enter a valid GSTIN (15-character Indian GSTIN)"
    ),
  pan: z
    .string()
    .optional()
    .refine(
      (v) => {
        if (!v) return true;
        const pan = v.trim().toUpperCase();
        return pan.length === 10 && PAN_REGEX.test(pan);
      },
      "Invalid PAN format (ABCDE1234F)"
    ),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      state: z.string().min(1, "State is required"),
      // city: z.string().min(1, "City is required"),
      city: z.string().optional(),
      pincode: z.string().optional(),
      country: z.string().optional(),
    })
    .optional(),
}).superRefine((data, ctx) => {
 const state = data?.address?.state?.trim();
  const city = data?.address?.city?.trim();
  const gstin = String(data?.gstin || "").trim().toUpperCase();
  const pan = String(data?.pan || "").trim().toUpperCase();
  if (!state) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["address", "state"],
      message: "State is required",
    });
    return; 
  }
  if (!city) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["address", "city"],
      message: "City is required",
    });
  }
  if (gstin && pan) {
    const panFromGstin = extractPanFromGstin(gstin);
    if (panFromGstin && panFromGstin !== pan) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["gstin"],
        message: "GSTIN does not match PAN",
      });
    }
  }

  const secondary = Array.isArray(data?.secondaryCustomers)
    ? data.secondaryCustomers
    : [];
  secondary.forEach((c, index) => {
    const sgstin = String(c?.customerGstin ?? c?.gstin ?? "").trim().toUpperCase();
    const span = String(c?.customerPan ?? c?.pan ?? "").trim().toUpperCase();
    if (sgstin && span) {
      const panFromGstin = extractPanFromGstin(sgstin);
      if (panFromGstin && panFromGstin !== span) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["secondaryCustomers", index, c?.customerGstin != null ? "customerGstin" : "gstin"],
          message: "GSTIN does not match PAN",
        });
      }
    }
  });
});

const COMPANY_COLUMNS = [
  { key: "companyCode", label: "Code" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  {
    key: "phone",
    label: "Phone",
    render: (val) => {
      const digits = String(val || "").replace(/\D/g, "").slice(-10);
      if (!digits) return "—";
      return `+91 ${digits}`;
    },
  },
  {
    key: "status",
    label: "Status",
    render: (val, row) => {
      const raw = row?.contract?.status ?? row?.status ?? "not_started";
      const normalized = String(raw).trim().toLowerCase();
      // Backward-compat mapping (old UI values -> new values). Keep frontend compile-safe.
      const status =
        normalized === "pending"
          ? "not_started"
          : normalized === "approved"
          ? "completed"
          : normalized === "rejected"
          ? "rejected"
          : normalized;
      let bg = "bg-amber-50";
      let text = "text-amber-700";
      let border = "border-amber-200";
      if (status === "completed") {
        bg = "bg-emerald-50";
        text = "text-emerald-700";
        border = "border-emerald-200";
      } else if (status === "rejected") {
        bg = "bg-red-50";
        text = "text-red-700";
        border = "border-red-200";
      }
      const label =
        status === "not_started"
          ? "Pending"
          : status === "completed"
          ? "Onboard"
          : status === "rejected"
          ? "Rejected"
          : String(status || "");
      return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${bg} ${text} ${border}`}
        >
          {label}
        </span>
      );
    },
  },
  {
    key: "onboardStatus",
    label: "Onboard Status",
    render: (val, row) => {
      const status = row?.contract?.onboardStatus || row?.onboardStatus || "pending";

      const colorMap = {
        pending: "bg-yellow-100 text-yellow-700",
        completed: "bg-green-100 text-green-700",
        rejected: "bg-red-100 text-red-700",
        cancelled: "bg-gray-100 text-gray-600",
      };

      return (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            colorMap[status] || colorMap.pending
          }`}
        >
          {status}
        </span>
      );
    },
  },
  {
    key: "address",
    label: "Address",
    render: (_, row) => {
      const a = row.address;
      if (!a) return "—";
      const parts = [a.line1, a.line2, a.city, a.state, a.pincode].filter(
        Boolean
      );
      return parts.length ? parts.join(", ") : "—";
    },
  },
];

export function CompanyManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyCode: "",
    name: "",
    email: "",
    phone: "",
    gstin: "",
    pan: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    secondaryCustomers: [],
  });
  const [currentSecondaryCustomer, setCurrentSecondaryCustomer] = useState({
    customerCode: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerGstin: "",
    customerAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
  });
  const [showSecondaryCustomerInputs, setShowSecondaryCustomerInputs] =
    useState(false);
  const [addressCityMismatch, setAddressCityMismatch] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState(null);
  const viewRowRef = useRef(null);

  const normalizeIndianPhone10 = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length > 10 && digits.startsWith("91")) {
      return digits.slice(2, 12);
    }
    return digits.slice(0, 10);
  };

  // useEffect(() => {
  //   fetchCompanies();
  // }, []);

  // useEffect(() => {
  //   const t = setTimeout(() => fetchCompanies(), 300);
  //   return () => clearTimeout(t);
  // }, [searchTerm]);
  // Debounce search and reset pagination on search change.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);
  const {
    data: companiesResponse,
    isLoading,
    isFetching,
    refetch: refetchCompanies,
  } = useQuery({
    queryKey: ["companies", debouncedSearchTerm, page, limit],
    queryFn: async () => {
      const params = { page, limit };
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      return companyService.getCompanies(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    const mappedCompanies = (companiesResponse?.data || []).map((company) => ({
      ...company,
      onboardStatus: company.contract?.onboardStatus || "pending",
    }));
    setCompanies(mappedCompanies);
  }, [companiesResponse]);
  const loading = isLoading || isFetching;
  const totalPages = Math.max(1, Number(companiesResponse?.totalPages) || 1);

  const resetForm = () => {
    setFormData({
      companyCode: "",
      name: "",
      email: "",
      phone: "",
      gstin: "",
      pan: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
      secondaryCustomers: [],
    });
    setCurrentSecondaryCustomer({
      customerCode: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerGstin: "",
      customerAddress: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
    setShowSecondaryCustomerInputs(false);
    setAddressCityMismatch(false);
    setEditingCompanyId(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const dataToSubmit = e && e.preventDefault ? formData : e;

    // Always send plain 10-digit phone numbers to backend (strip +91/display formatting).
    const normalizedPrimaryPhone = String(dataToSubmit?.phone || "")
      .replace(/\D/g, "")
      .slice(-10);
    const normalizedSecondaryCustomers = Array.isArray(dataToSubmit?.secondaryCustomers)
      ? dataToSubmit.secondaryCustomers.map((c) => ({
          ...c,
          customerPhone: String(c?.customerPhone || "")
            .replace(/\D/g, "")
            .slice(-10),
        }))
      : [];
    const sanitizedData = {
      ...dataToSubmit,
      phone: normalizedPrimaryPhone,
      secondaryCustomers: normalizedSecondaryCustomers,
    };

    // Keep secondary-customer edit validation aligned with create rules.
    for (const c of sanitizedData.secondaryCustomers || []) {
      const scName = String(c?.customerName || "").trim();
      const scPhone = String(c?.customerPhone || "").trim();
      const scState = String(c?.customerAddress?.state || "").trim();
      const scCity = String(c?.customerAddress?.city || "").trim();
      if (!scName || !NAME_REGEX.test(scName)) {
        toast.error("Secondary Customer Name can contain alphabets and spaces only");
        return;
      }
      if (!PHONE_REGEX.test(scPhone)) {
        toast.error("Enter a valid 10-digit Secondary Customer phone number");
        return;
      }
      if (!scState) {
        toast.error("State is required for Secondary Customer address");
        return;
      }
      if (!scCity) {
        toast.error("City is required for Secondary Customer address");
        return;
      }
    }

    const parsed = companyFormSchema.safeParse({
      ...sanitizedData,
      address: sanitizedData.address || {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      const msg = firstIssue?.message || "Validation failed";
      toast.error(msg);
      return;
    }

    try {
      if (editingCompanyId) {
        await companyService.updateCompany(editingCompanyId, sanitizedData);
        toast.success("Company updated successfully");
      } else {
        await companyService.createCompany(sanitizedData);
        toast.success("Company created successfully");
      }
      resetForm();
      setShowForm(false);
      await refetchCompanies();
    } catch (error) {
      toast.error(companySaveErrorMessage(error));
    }
  };

  // NEW: open company details view similar to Inward Management
  const openView = async (row) => {
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    try {
      const res = await companyService.getCompanyById(row._id || row.id);
      const company = res.data || res;
      setViewData(company);
    } catch (err) {
      console.error("Error fetching company details:", err);
      setViewError(err?.message || "Failed to load company details");
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleEdit = (row) => {
    const state = row.address?.state?.trim() || "";
    const city = row.address?.city?.trim() || "";
    const cityValidForState = state && city && isCityInState(city, state);
    setFormData({
      companyCode: row.companyCode || "",
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      gstin: row.gstin || "",
      pan: row.pan || "",
      address: {
        line1: row.address?.line1 || "",
        line2: row.address?.line2 || "",
        city: cityValidForState ? city : "",
        state,
        pincode: row.address?.pincode || "",
        country: row.address?.country || "India",
      },
      secondaryCustomers: row.secondaryCustomers || [],
    });
    setAddressCityMismatch(!!(state && city && !cityValidForState));
    setCurrentSecondaryCustomer({
      customerCode: "",
      customerName: "",
    });
    setShowSecondaryCustomerInputs(false);
    setEditingCompanyId(row._id || row.id);
    setShowForm(true);
  };
  const updateSecondaryCustomerField = (index, field, value) => {
    const updated = [...(formData.secondaryCustomers || [])];
    updated[index] = { ...(updated[index] || {}), [field]: value };
    setFormData({ ...formData, secondaryCustomers: updated });
  };

  const updateSecondaryCustomerAddressField = (index, field, value) => {
    const updated = [...(formData.secondaryCustomers || [])];
    const prev = updated[index] || {};
    const prevAddr = prev.customerAddress || {};
    updated[index] = {
      ...prev,
      customerAddress: {
        ...prevAddr,
        [field]: value,
        country: prevAddr.country || "India",
      },
    };
    setFormData({ ...formData, secondaryCustomers: updated });
  };
  const handleAddSecondaryCustomer = () => {
    const code = currentSecondaryCustomer.customerCode?.trim() || "";
    const name = currentSecondaryCustomer.customerName?.trim() || "";
    const email =
      currentSecondaryCustomer.customerEmail?.trim()?.toLowerCase() || "";
    const phone = currentSecondaryCustomer.customerPhone?.trim() || "";
    const gstin =
      currentSecondaryCustomer.customerGstin?.trim()?.toUpperCase() || "";

    if (!code) {
      toast.error("Customer Code is required");
      return;
    }

    if (!name) {
      toast.error("Customer Name is required");
      return;
    }
    if (!NAME_REGEX.test(name)) {
      toast.error("Customer Name can contain alphabets and spaces only");
      return;
    }

    const a = currentSecondaryCustomer.customerAddress || {};
    const state = a.state?.trim() || "";
    const city = a.city?.trim() || "";
    if (!state) {
      toast.error("State is required for Secondary Customer address");
      return;
    }
    if (!city) {
      toast.error("City is required for Secondary Customer address");
      return;
    }

    if (!phone) {
      toast.error("Secondary Customer Phone is required");
      return;
    }
    if (!PHONE_REGEX.test(phone)) {
      toast.error("Enter a valid 10-digit Secondary Customer phone number");
      return;
    }
    if (!gstin) {
      toast.error("Secondary Customer GSTIN is required");
      return;
    }
    if (!INDIAN_GSTIN_REGEX.test(gstin)) {
      toast.error(
        "Enter a valid Secondary Customer GSTIN (15-character Indian GSTIN)"
      );
      return;
    }

    // NEW: validate secondary customer email format when provided.
    if (email && !EMAIL_REGEX.test(email)) {
      toast.error("Enter a valid Secondary Customer email");
      return;
    }

    const existing = formData.secondaryCustomers || [];
    const codeExists = existing.some((c) => c.customerCode === code);
    const nameExists = existing.some(
      (c) => c.customerName.toLowerCase() === name.toLowerCase()
    );

    if (codeExists) {
      toast.error(`Customer Code "${code}" already exists`);
      return;
    }

    if (nameExists) {
      toast.error(`Customer Name "${name}" already exists`);
      return;
    }
    const customerAddress = {
      line1: a.line1?.trim() || "",
      line2: a.line2?.trim() || "",
      city: a.city?.trim() || "",
      state: a.state?.trim() || "",
      pincode: a.pincode?.trim() || "",
      country: a.country?.trim() || "India",
    };
    setFormData({
      ...formData,
      secondaryCustomers: [
        ...existing,
        {
          customerCode: code,
          customerName: name,
          customerEmail: email || undefined,
          customerPhone: phone || undefined,
          customerGstin: gstin || undefined,
          customerAddress,
          isActive: true,
        },
      ],
    });

    setCurrentSecondaryCustomer({
      customerCode: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerGstin: "",
      customerAddress: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
    setShowSecondaryCustomerInputs(false);
  };

  const handleRemoveSecondaryCustomer = (index) => {
    const updated = (formData.secondaryCustomers || []).filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      secondaryCustomers: updated,
    });
  };

  const handleDelete = (row) => {
    // Company delete disabled in UI — re-enable with Delete action + DeletePopUp + imports
    // setRowToDelete(row);
    // setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await companyService.deleteCompany(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      await refetchCompanies();
      toast.success("Company deactivated successfully");
    } catch (err) {
      console.error("Error deleting company:", err);
      toast.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const companyActions = [
    {
      label: "Edit",
      icon: EditIcon,
      onClick: handleEdit,
    },
    // Company delete disabled in UI — re-enable with DeleteIcon import + DeletePopUp
    // {
    //   label: "Delete",
    //   icon: DeleteIcon,
    //   onClick: handleDelete,
    // },
  ];

  const companyFields = [
    {
      name: "companyCode",
      label: "Primary Customer Code",
      type: "text",
      required: false,
      grid: "1",
      disabled: !!editingCompanyId,
      placeholder: "Optional–auto-generated",
    },
    {
      name: "name",
      label: "Primary Customer Name",
      type: "text",
      required: true,
      grid: "1",
      placeholder: "Enter Primary Name",
      // Restrict input to alphabets and spaces at HTML level where possible.
      pattern: "[A-Za-z ]*",
      inputMode: "text",
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      grid: "1",
      placeholder: "Enter Email",
    },
    // OLD: generic phone field relied on pattern/maxLength only (typing could exceed 10 digits in some cases).
    // {
    //   name: "phone",
    //   label: "Phone",
    //   type: "text",
    //   required: true,
    //   grid: "1",
    //   placeholder: "Enter phone number",
    //   // Restrict to digits on supported browsers and cap at 10 characters.
    //   inputMode: "numeric",
    //   pattern: "[0-9]*",
    //   maxLength: 10,
    // },
    // NEW: custom phone field – digits only, hard-capped at 10 during typing (both create & edit).
    {
      type: "custom",
      grid: "1",
      render: () => (
        <div>
          <label className="form-popup-label mb-1.5 block">
            Phone <span className="ml-1 text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.phone ? `+91 ${formData.phone}` : ""}
            onChange={(e) => {
              const raw = e.target.value || "";
              let digits = raw.replace(/\D/g, "");
              if (raw.trim().startsWith("+91")) {
                digits = digits.slice(2);
              }
              digits = digits.slice(0, 10);
              setFormData((prev) => ({
                ...prev,
                phone: digits.length === 0 ? "" : digits,
              }));
            }}
            inputMode="numeric"
            maxLength={14}
            className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
            style={{ height: 44, padding: "0 14px" }}
            placeholder="+91 enter your number"
          />
        </div>
      ),
    },
    {
      name: "gstin",
      label: "GSTIN Number",
      type: "text",
      required: true,
      grid: "1",
      placeholder: "GSTIN(07ABCDE1234F1Z5)",
    },
    {
      name: "pan",
      label: "PAN Number",
      type: "text",
      grid: "1",
      required: true,
      placeholder: "Enter PAN (ABCDE1234F)",
      transform: (v) => (v ? String(v).toUpperCase() : v), // optional if FormPopup supports
    },
    {
      name: "address.line1",
      label: "Address Line 1",
      type: "text",
      grid: "1",
      placeholder: "Enter Address line 1",
    },
    {
      name: "address.line2",
      label: "Address Line 2",
      type: "text",
      grid: "1",
      placeholder: "Enter Address Line 2",
    },
    {
      type: "custom",
      grid: "2",
      render: () => {
        const state = formData.address?.state ?? "";
        const city = formData.address?.city ?? "";
        // OLD: unsorted state/city options.
        // const cityOptions = getCitiesForState(state);
        // NEW: sort state and city dropdowns alphabetically.
        const stateOptions = [...INDIA_STATES].sort((a, b) =>
          String(a.label || "").localeCompare(String(b.label || ""))
        );
        const cityOptions = getCitiesForState(state).slice().sort((a, b) =>
          String(a.label || "").localeCompare(String(b.label || ""))
        );
        const cityDisabled = !state;
        return (
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div className="relative">
              <label className="form-popup-label mb-1.5 block">
                State <span className="ml-1 text-red-500">*</span>
              </label>
              <SearchableSelect
                value={state}
                onChange={(e) => {
                  const newState = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    address: {
                      ...(prev.address || {}),
                      state: newState,
                      city: "",
                      line1: prev.address?.line1,
                      line2: prev.address?.line2,
                      pincode: prev.address?.pincode,
                      country: prev.address?.country || "India",
                    },
                  }));
                  setAddressCityMismatch(false);
                }}
                // OLD: options={INDIA_STATES}
                // NEW: use alphabetically sorted state options
                options={stateOptions}
                placeholder="Select State"
              />
            </div>
            <div className="relative">
              <label className="form-popup-label mb-1.5 block">
                City <span className="ml-1 text-red-500">*</span>
              </label>
              <SearchableSelect
                value={city}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    address: {
                      ...(prev.address || {}),
                      city: e.target.value,
                      state: prev.address?.state,
                      line1: prev.address?.line1,
                      line2: prev.address?.line2,
                      pincode: prev.address?.pincode,
                      country: prev.address?.country || "India",
                    },
                  }));
                  setAddressCityMismatch(false);
                }}
                // OLD: options={cityOptions} without guaranteed sort
                // NEW: use alphabetically sorted city options
                options={cityOptions}
                placeholder="Select City"
                disabled={cityDisabled}
              />
              {addressCityMismatch && (
                <p className="mt-1.5 text-sm text-red-500">
                  Saved city does not belong to selected state. Please select a
                  city.
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    // OLD: plain pincode field allowed non-numeric characters while typing.
    // {
    //   name: "address.pincode",
    //   label: "Pincode",
    //   type: "text",
    //   grid: "1",
    //   placeholder: "Enter pincode",
    //   // NEW: restrict primary pincode to numeric input where possible.
    //   inputMode: "numeric",
    //   pattern: "[0-9]*",
    //   transform: (v) =>
    //     v != null ? String(v).replace(/\D/g, "") : v,
    // },
    // NEW: custom primary pincode field – digits only during typing.
    {
      type: "custom",
      grid: "1",
      render: () => (
        <div>
          <label className="form-popup-label mb-1.5 block">Pincode</label>
          <input
            type="text"
            value={formData.address?.pincode || ""}
            onChange={(e) => {
              const raw = e.target.value || "";
              const digitsOnly = raw.replace(/\D/g, "").slice(0, 6);
              setFormData((prev) => ({
                ...prev,
                address: {
                  ...(prev.address || {}),
                  pincode: digitsOnly,
                  country: prev.address?.country || "India",
                },
              }));
            }}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
            style={{ height: 44, padding: "0 14px" }}
            placeholder="Enter pincode"
          />
        </div>
      ),
    },
    {
      type: "custom",
      grid: "full",
      render: () =>
        !editingCompanyId && (
          <div
            className="secWrap"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 18,
            }}
          >
            <div style={{ alignSelf: "flex-end" }}>
              <button
                type="button"
                onClick={() =>
                  setShowSecondaryCustomerInputs(!showSecondaryCustomerInputs)
                }
                className="w-fit h-11 px-[16px] rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40 }}
              >
                Add Secondary Customer
              </button>
            </div>
            {showSecondaryCustomerInputs && (
              <div
                style={{
                  width: "100%",
                  display: "block",
                  border: "1px solid #E4E7EC",
                  borderRadius: 14,
                  padding: 18,
                  backgroundColor: "#FFFFFF",
                }}
              >
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#101828",
                    marginBottom: 16,
                  }}
                >
                  Seconday Customer
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                    columnGap: 26,
                    rowGap: 18,
                  }}
                >
                  <div className="relative">
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Customer Code
                    </label>
                    <input
                      type="text"
                      value={currentSecondaryCustomer.customerCode}
                      onChange={(e) =>
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerCode: e.target.value,
                        })
                      }
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter customer code"
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Customer Name
                    </label>
                    <input
                      type="text"
                    value={currentSecondaryCustomer.customerName}
                    onChange={(e) => {
                      // Allow alphabets and spaces only while typing.
                      const raw = e.target.value || "";
                      const sanitized = raw.replace(/[^A-Za-z\s]/g, "");
                      setCurrentSecondaryCustomer({
                        ...currentSecondaryCustomer,
                        customerName: sanitized,
                      });
                    }}
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter customer name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Customer Email
                    </label>
                    <input
                      type="email"
                      value={currentSecondaryCustomer.customerEmail}
                      onChange={(e) =>
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerEmail: e.target.value,
                        })
                      }
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Customer Phone
                    </label>
                    <input
                      type="tel"
                      value={
                        currentSecondaryCustomer.customerPhone
                          ? `+91 ${currentSecondaryCustomer.customerPhone}`
                          : ""
                      }
                      onChange={(e) => {
                        const raw = e.target.value || "";
                        let digits = raw.replace(/\D/g, "");
                        if (raw.trim().startsWith("+91")) {
                          digits = digits.slice(2);
                        }
                        digits = digits.slice(0, 10);
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerPhone: digits.length === 0 ? "" : digits,
                        });
                      }}
                      inputMode="numeric"
                      maxLength={14}
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="+91 enter your number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Customer GSTIN
                    </label>
                    <input
                      type="text"
                      value={currentSecondaryCustomer.customerGstin}
                      onChange={(e) =>
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerGstin: e.target.value,
                        })
                      }
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter GSTIN"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={
                        currentSecondaryCustomer.customerAddress?.line1 || ""
                      }
                      onChange={(e) =>
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerAddress: {
                            ...(currentSecondaryCustomer.customerAddress || {}),
                            line1: e.target.value,
                            country:
                              currentSecondaryCustomer.customerAddress
                                ?.country || "India",
                          },
                        })
                      }
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter address line 1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={
                        currentSecondaryCustomer.customerAddress?.line2 || ""
                      }
                      onChange={(e) =>
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerAddress: {
                            ...(currentSecondaryCustomer.customerAddress || {}),
                            line2: e.target.value,
                            country:
                              currentSecondaryCustomer.customerAddress
                                ?.country || "India",
                          },
                        })
                      }
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter address line 2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={
                        currentSecondaryCustomer.customerAddress?.state || ""
                      }
                      onChange={(e) => {
                        const newState = e.target.value;
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerAddress: {
                            ...(currentSecondaryCustomer.customerAddress || {}),
                            state: newState,
                            city: "",
                            country:
                              currentSecondaryCustomer.customerAddress
                                ?.country || "India",
                          },
                        });
                      }}
                      // OLD: options={INDIA_STATES}
                      // NEW: sorted state options for secondary section
                      options={[...INDIA_STATES].sort((a, b) =>
                        String(a.label || "").localeCompare(String(b.label || ""))
                      )}
                      placeholder="Select State"
                    />
                    {/* ContainedSelect replaced with SearchableSelect for state search in secondary customer */}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={
                        currentSecondaryCustomer.customerAddress?.city || ""
                      }
                      onChange={(e) =>
                        setCurrentSecondaryCustomer({
                          ...currentSecondaryCustomer,
                          customerAddress: {
                            ...(currentSecondaryCustomer.customerAddress || {}),
                            city: e.target.value,
                            state:
                              currentSecondaryCustomer.customerAddress?.state,
                            country:
                              currentSecondaryCustomer.customerAddress
                                ?.country || "India",
                          },
                        })
                      }
                      // OLD: direct getCitiesForState() without guaranteed sort
                      // NEW: sort city list alphabetically for secondary section
                      options={getCitiesForState(
                        currentSecondaryCustomer.customerAddress?.state || ""
                      )
                        .slice()
                        .sort((a, b) =>
                          String(a.label || "").localeCompare(String(b.label || ""))
                        )}
                      placeholder="Select City"
                      disabled={
                        !currentSecondaryCustomer.customerAddress?.state
                      }
                    />
                    {/* ContainedSelect replaced with SearchableSelect for city search in secondary customer */}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#344054] mb-2">
                      Pincode
                    </label>
                    <input
                      type="text"
                      value={
                        currentSecondaryCustomer.customerAddress?.pincode || ""
                      }
                    onChange={(e) =>
                      setCurrentSecondaryCustomer({
                        ...currentSecondaryCustomer,
                        customerAddress: {
                          ...(currentSecondaryCustomer.customerAddress || {}),
                          // NEW: allow digits only for secondary pincode while typing.
                          pincode: (e.target.value || "").replace(/\D/g, "").slice(0, 6),
                          country:
                            currentSecondaryCustomer.customerAddress
                              ?.country || "India",
                        },
                      })
                    }
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                      className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                      style={{ height: 44, padding: "0 14px" }}
                      placeholder="Enter pincode"
                    />
                  </div>
                </div>
                <div
                  className="flex justify-end"
                  style={{ gap: 16, marginTop: 18 }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowSecondaryCustomerInputs(false);
                      setCurrentSecondaryCustomer({
                        customerCode: "",
                        customerName: "",
                        customerEmail: "",
                        customerPhone: "",
                        customerGstin: "",
                        customerAddress: {
                          line1: "",
                          line2: "",
                          city: "",
                          state: "",
                          pincode: "",
                          country: "India",
                        },
                      });
                    }}
                    className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                    style={{ width: 200, height: 44 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddSecondaryCustomer}
                    className="rounded-[10px] bg-[#188742] text-white font-semibold transition hover:bg-[#15733a]"
                    style={{ width: 200, height: 44 }}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ),
    },
    {
      type: "custom",
      grid: "full",
      render: () =>
        formData.secondaryCustomers &&
        formData.secondaryCustomers.length > 0 && (
          <div
            className="mb-5"
            style={{
              border: "2px dashed rgba(24,135,66,0.55)",
              borderRadius: 14,
              backgroundColor: "rgba(24,135,66,0.04)",
              padding: 16,
            }}
          >
            <div
              className="mb-2"
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "#344054",
              }}
            >
              Added Secondary Customers : {formData.secondaryCustomers.length}
            </div>
            <div className="overflow-x-auto rounded-[14px] border border-[#EEF1F5] bg-white p-[14px]">
              <table
                className="w-full border-collapse"
                style={editingCompanyId ? { minWidth: 1800 } : undefined}
              >
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                      Customer Code
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                      Customer Name
                    </th>
                    {editingCompanyId && (
                      <>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          Phone
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          GSTIN
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          State
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          City
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          Pincode
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          Address Line 1
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                          Address Line 2
                        </th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formData.secondaryCustomers.map((customer, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="px-4 py-2 text-sm text-gray-700 min-w-[180px]">
                        {editingCompanyId ? (
                          <input
                            type="text"
                            value={customer.customerCode || ""}
                            onChange={(e) =>
                              updateSecondaryCustomerField(
                                index,
                                "customerCode",
                                e.target.value
                              )
                            }
                            className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                          />
                        ) : (
                          customer.customerCode
                        )}
                      </td>

                      <td className="px-4 py-2 text-sm text-gray-700 min-w-[220px]">
                        {editingCompanyId ? (
                          <input
                            type="text"
                            value={customer.customerName || ""}
                            onChange={(e) =>
                              updateSecondaryCustomerField(
                                index,
                                "customerName",
                                e.target.value
                              )
                            }
                            className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                          />
                        ) : (
                          customer.customerName
                        )}
                      </td>
                      {editingCompanyId && (
                        <>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[240px]">
                            <input
                              type="email"
                              value={customer.customerEmail || ""}
                              onChange={(e) =>
                                updateSecondaryCustomerField(
                                  index,
                                  "customerEmail",
                                  e.target.value
                                )
                              }
                              className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                              placeholder="Enter email"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[190px]">
                            <input
                              type="tel"
                              value={customer.customerPhone ? `+91 ${customer.customerPhone}` : ""}
                              onChange={(e) => {
                                const normalized = normalizeIndianPhone10(e.target.value);
                                updateSecondaryCustomerField(index, "customerPhone", normalized);
                              }}
                              inputMode="numeric"
                              maxLength={14}
                              placeholder="+91 enter your number"
                              className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[220px]">
                            <input
                              type="text"
                              value={customer.customerGstin || ""}
                              onChange={(e) =>
                                updateSecondaryCustomerField(
                                  index,
                                  "customerGstin",
                                  e.target.value
                                )
                              }
                              className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                              placeholder="Enter GSTIN"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[180px]">
                            <SearchableSelect
                              value={customer.customerAddress?.state || ""}
                              onChange={(e) => {
                                const nextState = e.target.value;
                                updateSecondaryCustomerAddressField(index, "state", nextState);
                                updateSecondaryCustomerAddressField(index, "city", "");
                              }}
                              options={[...INDIA_STATES].sort((a, b) =>
                                String(a.label || "").localeCompare(String(b.label || ""))
                              )}
                              placeholder="Select State"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[180px]">
                            <SearchableSelect
                              value={customer.customerAddress?.city || ""}
                              onChange={(e) =>
                                updateSecondaryCustomerAddressField(index, "city", e.target.value)
                              }
                              options={getCitiesForState(customer.customerAddress?.state || "")
                                .slice()
                                .sort((a, b) =>
                                  String(a.label || "").localeCompare(String(b.label || ""))
                                )}
                              placeholder="Select City"
                              disabled={!customer.customerAddress?.state}
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[150px]">
                            <input
                              type="text"
                              value={customer.customerAddress?.pincode || ""}
                              onChange={(e) =>
                                updateSecondaryCustomerAddressField(
                                  index,
                                  "pincode",
                                  (e.target.value || "").replace(/\D/g, "").slice(0, 6)
                                )
                              }
                              inputMode="numeric"
                              maxLength={6}
                              className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                              placeholder="Enter pincode"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[240px]">
                            <input
                              type="text"
                              value={customer.customerAddress?.line1 || ""}
                              onChange={(e) =>
                                updateSecondaryCustomerAddressField(index, "line1", e.target.value)
                              }
                              className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                              placeholder="Enter address line 1"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 min-w-[240px]">
                            <input
                              type="text"
                              value={customer.customerAddress?.line2 || ""}
                              onChange={(e) =>
                                updateSecondaryCustomerAddressField(index, "line2", e.target.value)
                              }
                              className="w-full min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 overflow-visible"
                              placeholder="Enter address line 2"
                            />
                          </td>
                        </>
                      )}
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveSecondaryCustomer(index)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium transition"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
    },
  ];

  return (
    <div className="page-container font-nunito">
      {/* ✅ Header (same as screenshot) */}
      <div className="mb-6 w-full">
        <div
          className="rounded-[28px] border border-[#EEF1F5] bg-[#FFFFFF] px-6 py-4 shadow-sm"
          style={{ boxShadow: "0px 0px 4px 0px #959DA526" }}
        >
          <div className="flex items-center justify-between w-full flex-wrap gap-4">
            {/* Left: Title */}
            <div className="min-w-0">
              <h1
                style={{
                  fontFamily: "Nunito",
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#161616",
                  margin: 0,
                }}
                className="whitespace-nowrap"
              >
                Company Management
              </h1>
              {/* ✅ subheading removed */}
            </div>

            {/* Right: Search + Button */}
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
              {/* Search */}
              <div className="w-full sm:w-[320px]">
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {/* search icon */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by Code"
                    className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 placeholder:text-gray-400 shadow-sm focus:border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </div>

              {/* Button */}
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="h-11 shrink-0 rounded-xl bg-[#188742] px-6 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
              >
                Create Company
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Company delete disabled in UI — re-enable DeletePopUp + DeleteIcon + companyActions Delete + handleDelete body */}
      {/* <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => {
          setDeletePopUpOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        entityLabel="company"
      /> */}
      <FormPopup
        open={showForm}
        title={editingCompanyId ? "Edit Company" : "Add New Company"}
        onClose={() => {
          setShowForm(false);
          resetForm();
        }}
        onSubmit={handleSubmit}
        submitText={editingCompanyId ? "Update" : "Create"}
        values={formData}
        setValues={setFormData}
        fields={companyFields}
        width="xl"
      />

      <CompanyDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData(null);
          setViewError(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />
      {loading ? (
        <SkeletonTable cols={COMPANY_COLUMNS.length + 1} rows={8} />
      ) : (
        <Table
          columns={COMPANY_COLUMNS}
          data={companies}
          loading={false}
          emptyMessage="No companies found"
          actions={companyActions}
          onRowClick={(row) => openView(row)}
        />
      )}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
          className="h-9 rounded-lg border border-[#D0D5DD] bg-white px-3 text-sm text-[#344054] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span className="text-sm text-[#667085]">{`Page ${page} of ${totalPages}`}</span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
          className="h-9 rounded-lg border border-[#D0D5DD] bg-white px-3 text-sm text-[#344054] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}
